import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Save, ImagePlus, X, Plus, Trash2, FolderPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetCategoriesQuery,
} from "@/store";
import { toast } from "sonner";
import type { CreateProductData, ProductVariation, ProductVariationOption } from "@/types/product";
import type { Media } from "@/types";
import { MediaSelector } from "@/components/media";

interface ProductFormData extends CreateProductData {
  // Form fields
}

export function ProductFormScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);
  const [variations, setVariations] = useState<ProductVariation[]>([]);
  const formPopulatedRef = useRef<string | false>(false);

  const { data: productData, isLoading: isLoadingProduct } =
    useGetProductByIdQuery(id || "", {
      skip: !id,
    });
  const { data: categoriesData } = useGetCategoriesQuery({});
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  const categories = categoriesData?.data || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormData>({
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      originalPrice: undefined,
      image: "",
      images: [],
      categoryId: "",
      inStock: true,
      stockQuantity: 0,
      badge: "",
      rating: undefined,
      onSale: false,
      featured: false,
    },
  });

  const categoryId = watch("categoryId");

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && productData?.data) {
      const product = productData.data;
      const productId = product.id;
      
      // Only populate if we haven't populated for this product ID
      if (formPopulatedRef.current === productId) {
        return;
      }
      
      // Set all form values
      setValue("name", product.name);
      setValue("description", product.description);
      setValue("price", product.price);
      setValue("originalPrice", product.originalPrice || undefined);
      setValue("image", product.image);
      setValue("images", product.images || []);
      setValue("inStock", product.inStock);
      setValue("stockQuantity", product.stockQuantity);
      setValue("badge", product.badge || "");
      setValue("rating", product.rating || undefined);
      setValue("onSale", product.onSale || false);
      setValue("featured", product.featured);
      
      // Ensure variations are properly set with all required fields
      if (product.variations && Array.isArray(product.variations)) {
        const processedVariations = product.variations.map(variation => ({
          ...variation,
          options: (variation.options || []).map(option => ({ ...option }))
        }));
        setVariations(processedVariations);
      } else {
        setVariations([]);
      }
      
      formPopulatedRef.current = productId;
    }
  }, [isEditMode, productData, setValue]);

  // Set categoryId separately after categories are loaded
  useEffect(() => {
    if (isEditMode && productData?.data && categories.length > 0) {
      const product = productData.data;
      const currentCategoryId = watch("categoryId");
      
      // Only set if it's not already set correctly
      if (product.categoryId && currentCategoryId !== product.categoryId) {
        // Verify the category exists in the list
        const categoryExists = categories.some((c) => c.id === product.categoryId);
        if (categoryExists) {
          setValue("categoryId", product.categoryId, { shouldValidate: true, shouldDirty: true });
        }
      }
    }
  }, [isEditMode, productData, categories, setValue, watch]);

  // Reset the ref when switching between edit and create mode
  useEffect(() => {
    if (!isEditMode) {
      formPopulatedRef.current = false;
    }
  }, [isEditMode]);

  const addVariation = () => {
    setVariations([
      ...variations,
      {
        id: `temp-${Date.now()}`,
        productId: id || "",
        name: "",
        required: false,
        order: variations.length,
        options: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ]);
  };

  const removeVariation = (index: number) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

  const updateVariation = (index: number, field: keyof ProductVariation, value: any) => {
    const updated = variations.map((variation, i) => 
      i === index ? { ...variation, [field]: value } : variation
    );
    setVariations(updated);
  };

  const addVariationOption = (variationIndex: number) => {
    const updated = variations.map((variation, i) => {
      if (i === variationIndex) {
        return {
          ...variation,
          options: [
            ...(variation.options || []),
            {
              id: `temp-${Date.now()}`,
              variationId: variation.id,
              name: "",
              price: 0,
              inStock: true,
              order: (variation.options || []).length,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ]
        };
      }
      return variation;
    });
    setVariations(updated);
  };

  const removeVariationOption = (variationIndex: number, optionIndex: number) => {
    const updated = variations.map((variation, i) => {
      if (i === variationIndex) {
        return {
          ...variation,
          options: variation.options.filter((_, optionIdx) => optionIdx !== optionIndex)
        };
      }
      return variation;
    });
    setVariations(updated);
  };

  const updateVariationOption = (
    variationIndex: number,
    optionIndex: number,
    field: keyof ProductVariationOption,
    value: any
  ) => {
    const updated = variations.map((variation, i) => {
      if (i === variationIndex) {
        return {
          ...variation,
          options: variation.options.map((option, optIdx) => 
            optIdx === optionIndex ? { ...option, [field]: value } : option
          )
        };
      }
      return variation;
    });
    setVariations(updated);
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      const productData: CreateProductData = {
        ...data,
        price: data.price,
        originalPrice: data.originalPrice || undefined,
        rating: data.rating || undefined,
        variations: variations.length > 0 ? variations : undefined,
      };

      if (isEditMode && id) {
        await updateProduct({ id, data: productData }).unwrap();
        toast.success("Product updated successfully");
      } else {
        await createProduct(productData).unwrap();
        toast.success("Product created successfully");
      }
      navigate("/admin/products");
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        `Failed to ${isEditMode ? "update" : "create"} product`;
      toast.error(errorMessage);
    }
  };

  const isLoading = isLoadingProduct || isCreating || isUpdating;
  const onSale = watch("onSale");
  const featured = watch("featured");
  const inStock = watch("inStock");

  if (isEditMode && isLoadingProduct) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/products")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode ? "Edit Product" : "Create Product"}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode
                ? "Update product information"
                : "Add a new product to your catalog"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Essential product details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Product Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Jollof Rice"
                    {...register("name", {
                      required: "Product name is required",
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your product..."
                    rows={5}
                    {...register("description", {
                      required: "Description is required",
                    })}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="badge">
                      Badge Text (Optional)
                    </Label>
                    <Input
                      id="badge"
                      placeholder="e.g., Popular, New"
                      {...register("badge")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rating">
                      Manual Rating (Optional)
                    </Label>
                    <Input
                      id="rating"
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      placeholder="e.g., 4.8"
                      {...register("rating", {
                        min: { value: 0, message: "Min rating is 0" },
                        max: { value: 5, message: "Max rating is 5" },
                        valueAsNumber: true,
                      })}
                    />
                    {errors.rating && (
                      <p className="text-sm text-destructive">{errors.rating.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="categoryId">
                      Category <span className="text-destructive">*</span>
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => navigate("/admin/categories/new")}
                    >
                      <FolderPlus className="h-3 w-3 mr-1" />
                      New Category
                    </Button>
                  </div>
                  <Select
                    value={categoryId || ""}
                    onValueChange={(value) => setValue("categoryId", value, { shouldValidate: true })}
                    key={`category-select-${categoryId || "empty"}-${categories.length}`}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          Loading categories...
                        </div>
                      ) : (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.categoryId && (
                    <p className="text-sm text-destructive">
                      {errors.categoryId.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle>Pricing</CardTitle>
                <CardDescription>Set product pricing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="price">
                    Price (₦) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    {...register("price", {
                      required: "Price is required",
                      min: { value: 0, message: "Price must be positive" },
                      valueAsNumber: true,
                    })}
                  />
                  {errors.price && (
                    <p className="text-sm text-destructive">{errors.price.message}</p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="onSale"
                    checked={onSale}
                    onCheckedChange={(checked) =>
                      setValue("onSale", checked === true)
                    }
                  />
                  <Label htmlFor="onSale" className="cursor-pointer">
                    Product is on sale
                  </Label>
                </div>

                {onSale && (
                  <div className="space-y-2">
                    <Label htmlFor="originalPrice">Original Price (₦)</Label>
                    <Input
                      id="originalPrice"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      {...register("originalPrice", {
                        min: { value: 0, message: "Price must be positive" },
                        valueAsNumber: true,
                      })}
                    />
                    {errors.originalPrice && (
                      <p className="text-sm text-destructive">
                        {errors.originalPrice.message}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Image */}
            <Card>
              <CardHeader>
                <CardTitle>Product Image</CardTitle>
                <CardDescription>Select or upload a product image</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="hidden"
                  {...register("image", {
                    required: "Image is required",
                  })}
                />
                
                {watch("image") ? (
                  <div className="relative rounded-md border overflow-hidden group">
                    <img
                      src={watch("image")}
                      alt="Product preview"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x300?text=Image+Error";
                      }}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setMediaSelectorOpen(true)}
                      >
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setValue("image", "")}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                    <p className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-xs text-white truncate">
                      {watch("image")}
                    </p>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setMediaSelectorOpen(true)}
                  >
                    <ImagePlus className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">Select Product Image</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Click to browse media library
                    </p>
                  </div>
                )}

                {errors.image && (
                  <p className="text-sm text-destructive">{errors.image.message}</p>
                )}
              </CardContent>
            </Card>

            {/* Media Selector Dialog */}
            <MediaSelector
              open={mediaSelectorOpen}
              onOpenChange={setMediaSelectorOpen}
              onSelect={(media) => {
                const selectedMedia = media as Media;
                setValue("image", selectedMedia.url, { shouldValidate: true });
              }}
              folder="products"
              title="Select Product Image"
              description="Choose an image from your media library or upload a new one"
            />

            {/* Inventory */}
            <Card>
              <CardHeader>
                <CardTitle>Inventory</CardTitle>
                <CardDescription>Stock management</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="inStock"
                    checked={inStock}
                    onCheckedChange={(checked) =>
                      setValue("inStock", checked === true)
                    }
                  />
                  <Label htmlFor="inStock" className="cursor-pointer">
                    Product is in stock
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stockQuantity">Stock Quantity</Label>
                  <Input
                    id="stockQuantity"
                    type="number"
                    min="0"
                    placeholder="0"
                    {...register("stockQuantity", {
                      min: { value: 0, message: "Quantity must be positive" },
                      valueAsNumber: true,
                    })}
                  />
                  {errors.stockQuantity && (
                    <p className="text-sm text-destructive">
                      {errors.stockQuantity.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <Card>
              <CardHeader>
                <CardTitle>Options</CardTitle>
                <CardDescription>Product settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured"
                    checked={featured}
                    onCheckedChange={(checked) =>
                      setValue("featured", checked === true)
                    }
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    Featured product
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="badge">Badge</Label>
                  <Input
                    id="badge"
                    placeholder="e.g., New, Popular, Chef's Pick"
                    {...register("badge")}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Variations */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Product Variations</CardTitle>
                <CardDescription>
                  Add variation types (e.g., Size, Spice Level) with options
                </CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addVariation}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Variation
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {variations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No variations added. Click "Add Variation" to create one.
              </p>
            ) : (
              variations.map((variation, variationIndex) => (
                <div
                  key={`${variation.id}-${variationIndex}`}
                  className="border rounded-lg p-4 space-y-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>
                          Variation Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          value={variation.name || ""}
                          onChange={(e) =>
                            updateVariation(variationIndex, "name", e.target.value)
                          }
                          placeholder="e.g., Size, Spice Level"
                        />
                      </div>
                      <div className="flex items-center space-x-2 pt-8">
                        <Checkbox
                          checked={variation.required}
                          onCheckedChange={(checked) =>
                            updateVariation(variationIndex, "required", checked === true)
                          }
                        />
                        <Label className="cursor-pointer">Required</Label>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVariation(variationIndex)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Options</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addVariationOption(variationIndex)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Option
                      </Button>
                    </div>
                    {variation.options.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No options added. Click "Add Option" to create one.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {variation.options.map((option, optionIndex) => (
                          <div
                            key={`${option.id}-${optionIndex}`}
                            className="flex items-center gap-2 p-2 border rounded"
                          >
                            <Input
                              value={option.name || ""}
                              onChange={(e) =>
                                updateVariationOption(
                                  variationIndex,
                                  optionIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              placeholder="Option name"
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={option.price || 0}
                              onChange={(e) =>
                                updateVariationOption(
                                  variationIndex,
                                  optionIndex,
                                  "price",
                                  e.target.value ? parseFloat(e.target.value) : 0
                                )
                              }
                              placeholder="Price (₦)"
                              className="w-32"
                            />
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={option.inStock}
                                onCheckedChange={(checked) =>
                                  updateVariationOption(
                                    variationIndex,
                                    optionIndex,
                                    "inStock",
                                    checked === true
                                  )
                                }
                              />
                              <Label className="text-xs">In Stock</Label>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                removeVariationOption(variationIndex, optionIndex)
                              }
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/products")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} className="gap-2">
            <Save className="h-4 w-4" />
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Product"
              : "Create Product"}
          </Button>
        </div>
      </form>
    </div>
  );
}
